/**
 * SentimentTemplate.tsx — AI Sentiment Analysis.
 * Covers: sentiment_analysys.
 *
 * Note on the payload: `entity` arrives as a single comma-separated STRING
 * ("Muslims, Chinese, Russians"), not an array, so it is split here. Worth
 * normalising at source if you ever want to filter or count entities.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import { prepareSentimentalAnalysisResult } from '../vendor/searcherService'
import {
  Chips, Cols, NoData, RawCard, ResCard, Stack, Stats, Verdict, useTokens,
} from '../ui/primitives'
import type { TemplateProps } from '../registry'
import { radii } from '../tokens'

const TONE: Record<string, { tone: 'ok' | 'bad' | 'warn'; label: string }> = {
  POSITIVE: { tone: 'ok', label: 'Positive' },
  NEGATIVE: { tone: 'bad', label: 'Negative' },
  NEUTRAL: { tone: 'warn', label: 'Neutral' },
  MIXED: { tone: 'warn', label: 'Mixed' },
}

export function SentimentTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()

  const item: any = React.useMemo(() => {
    try {
      const r = prepareSentimentalAnalysisResult(apiData as any)
      return Array.isArray(r?.data) && r.data.length ? r.data[0] : null
    } catch {
      return null
    }
  }, [apiData])

  if (!item) return <NoData title="No sentiment returned" hint="The analyser produced no output for this text." />

  const key = String(item.sentiment ?? '').toUpperCase()
  const verdict = TONE[key] || { tone: 'warn' as const, label: item.sentiment || 'Unknown' }
  const entities = String(item.entity ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

  const face =
    verdict.tone === 'ok'
      ? <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" />
      : verdict.tone === 'bad'
      ? <path d="M8 15.5s1.5-2 4-2 4 2 4 2M9 9h.01M15 9h.01" />
      : <path d="M8 15h8M9 9h.01M15 9h.01" />

  return (
    <>
      <ResCard title="Verdict" tone={verdict.tone === 'bad' ? 'bad' : verdict.tone === 'ok' ? 'good' : 'accent'}>
        <Verdict
          tone={verdict.tone}
          title={verdict.label}
          subtitle={`status ${item.status ?? '—'}`}
          icon={<svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" />{face}</svg>}
        >
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Stats
              items={[
                { label: 'Topic', value: item.topic || '—', tone: verdict.tone === 'bad' ? 'bad' : undefined },
                { label: 'Entities', value: entities.length, sub: 'referenced', tone: verdict.tone === 'bad' ? 'bad' : undefined },
              ]}
            />
          </Box>
        </Verdict>
      </ResCard>

      <Cols n={2}>
        <Stack>
          {entities.length > 0 && (
            <ResCard title="Entities detected" count={entities.length}>
              <Chips items={entities} tone={verdict.tone === 'bad' ? 'd' : 'b'} />
              <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '9px' }}>
                Groups or names referenced in the analysed text.
              </Typography>
            </ResCard>
          )}
          {item.topic && (
            <ResCard title="Topic">
              <Typography sx={{ fontWeight: 800, fontSize: 17, color: tk.text }}>{item.topic}</Typography>
              <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '6px' }}>Single label returned by the model.</Typography>
            </ResCard>
          )}
        </Stack>

        <Stack>
          {item.summary && (
            <ResCard title="Summary">
              <Typography sx={{ fontSize: 13.5, lineHeight: 1.65, color: tk.text2 }}>{item.summary}</Typography>
            </ResCard>
          )}
          {item.text && (
            <ResCard title="Analysed text">
              <Box sx={{ background: tk.surface2, border: `1px solid ${tk.border}`, borderRadius: `${radii.md}px`, p: '12px', fontSize: 13, fontStyle: 'italic', color: tk.text2 }}>
                “{item.text}”
              </Box>
              <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '9px' }}>Submitted by the user as the search criteria.</Typography>
            </ResCard>
          )}
        </Stack>
      </Cols>

      <RawCard data={apiData} />
    </>
  )
}
